import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// components
import {
    Paper,
    Tooltip,
    IconButton,
    Button,
    TextField,
    TableContainer,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@material-ui/core";
import BouncingDots from "../loading/BouncingDots";

import { IAppState } from "../../types/stores";
import { IEnrichmentField, IEnrichmentFieldsItem } from "../../types/stores/umbrellaData";
import {
    AddAdditionalPersons,
    searchEnrichmentFields,
    setEnrichmentFields,
    setSelectedEnrichmentFieldsItem,
    setSelectedEnrichmentItemId,
    UpdateServiceParameter,
} from "../../actions/flowControlActions";

import { updateAttribute } from "../../actions/flowActions";
import { getFlowExternalServiceForSelectedFlowItem } from "../../reducers/flowExternalServices";
import PersonNumberSelection from "../shared/PersonNumberSelection";
import { IFieldPerson } from "../../types/stores/flowControlData";

type Props = {
    flowItemId: number;
    flowServiceId: number;
};

const UEFieldSearch: React.FC<Props> = ({ flowItemId, flowServiceId }: Props) => {
    const dispatch = useDispatch();

    const [searchValue, setSearchValue] = useState("");
    const [additionalPersons, setAdditionalPersons] = useState<Array<IFieldPerson>>([]);

    const availableFields = useSelector<IAppState, Array<IEnrichmentField>>(
        state => state.umbrellaData.enrichmentFields
    );
    const selectedFields = useSelector<IAppState, Array<IEnrichmentFieldsItem>>(
        state => state.umbrellaData.selectedFieldsItem
    );
    const isSearching = useSelector((state: IAppState) => state.umbrellaData.isSearching);
    const flowExternalService = useSelector((state: IAppState) => getFlowExternalServiceForSelectedFlowItem(state));

    const externalService = useSelector((state: IAppState) => getFlowExternalServiceForSelectedFlowItem(state));

    const handleAddField = (field: IEnrichmentFieldsItem) => {
        let itemFields = [...selectedFields];
        let persons: Array<IFieldPerson> = [];
        if (field.LevelId == 7) {
            if (
                !additionalPersons ||
                additionalPersons.length == 0 ||
                additionalPersons.filter(x => x.FieldId == field.FieldId).length == 0
            ) {
                persons = updateAdditionalPersons(field.FieldId, externalService?.AdditionalPersons);
            } else {
                persons = [...additionalPersons];
            }
        } else {
            persons = updateAdditionalPersons(field.FieldId, "none");
        }

        // Always add Field_List
        dispatch(UpdateServiceParameter(field.FieldId, "Field_List"));

        // Add to selectedFields
        const addedField = availableFields.find(x => x.FieldId == field.FieldId);
        let copiedField: IEnrichmentFieldsItem;
        copiedField = Object.assign({}, addedField);
        copiedField.FlowItemId = flowItemId;
        copiedField.ServiceId = flowServiceId;
        if (copiedField) {
            itemFields.push(copiedField);
            dispatch(setSelectedEnrichmentFieldsItem(itemFields));
            dispatch(setSelectedEnrichmentItemId(flowItemId));
        }

        dispatch(
            updateAttribute(
                "flowExternalServices",
                flowExternalService.FlowExternalServiceId,
                "UECompliancePassed",
                false
            )
        );

        // Add Additonal Persons if selected
        dispatch(AddAdditionalPersons(persons, field.FieldId));
    };

    const clearSearch = () => {
        setSearchValue("");
        setAdditionalPersons([]);
        dispatch(setEnrichmentFields([]));
    };

    const handlePersonChanged = (fieldId, selectedPersonNumber) => {
        const persons = updateAdditionalPersons(fieldId, selectedPersonNumber);
        setAdditionalPersons(persons);
    };

    const updateAdditionalPersons = (fieldId, selectedPersonNumber) => {
        let persons: Array<IFieldPerson> = [...additionalPersons];
        const objIndex = additionalPersons.findIndex(obj => obj.FieldId == fieldId);

        if (objIndex < 0) {
            persons.push({ FieldId: parseInt(fieldId), PersonNumber: selectedPersonNumber });
        } else {
            persons[objIndex].PersonNumber = selectedPersonNumber;
        }
        return persons;
    };

    const fieldSelected = fieldId =>
        selectedFields.filter(x => x.FieldId == fieldId && x.FlowItemId == flowItemId && x.ServiceId == flowServiceId)
            .length > 0;

    const renderAddLink = (field: IEnrichmentField) => (
        <Tooltip title="Add Field">
            <IconButton aria-label="delete" color="secondary" size="small" onClick={() => handleAddField(field)}>
                <i className="material-icons" style={{ height: "25px", width: "25px", fontSize: "25px" }}>
                    add
                </i>
            </IconButton>
        </Tooltip>
    );

    const renderSelected = () => (
        <Tooltip title="Field Selected">
            <i className="material-icons" style={{ height: "25px", width: "25px", fontSize: "25px", color: "#406eb3" }}>
                check
            </i>
        </Tooltip>
    );

    const renderAdditionalPerson = fieldId => (
        <PersonNumberSelection
            style={{ width: "75px", margin: "0 50px" }}
            onChange={handlePersonChanged}
            defaultValue={externalService?.AdditionalPersons ?? "none"}
            id={fieldId}
        />
    );

    const renderFieldTable = () => (
        <div style={{ marginBottom: "20px" }}>
            <Paper>
                <TableContainer style={{ maxHeight: "55vh" }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    align="left"
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 16,
                                        alignSelf: "left",
                                        width: 125,
                                    }}
                                >
                                    Field Id
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 16,
                                        alignSelf: "left",
                                    }}
                                >
                                    Field Name
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 16,
                                        alignSelf: "left",
                                    }}
                                >
                                    Description
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 16,
                                        alignSelf: "left",
                                        width: 175,
                                    }}
                                >
                                    Additional Persons
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 16,
                                        alignSelf: "left",
                                    }}
                                >
                                    Add
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {availableFields.length == 0 ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell
                                        style={{
                                            fontSize: 18,
                                            alignSelf: "center",
                                            padding: "15px 16px 15px 16px",
                                            fontWeight: 400,
                                        }}
                                    >
                                        No Results
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        ) : (
                            <TableBody>
                                {availableFields.map((field, i) => (
                                    <TableRow
                                        key={i}
                                        style={fieldSelected(field.FieldId) ? { backgroundColor: "#DCDCDC" } : {}}
                                    >
                                        <TableCell
                                            align="left"
                                            style={{
                                                fontSize: 14,
                                                alignSelf: "left",
                                                padding: "5px 16px 5px 16px",
                                            }}
                                        >
                                            {field.FieldId}
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            style={{
                                                fontSize: 14,
                                                alignSelf: "left",
                                                padding: "5px 16px 5px 16px",
                                                whiteSpace: "nowrap",
                                                maxWidth: "400px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {field.FieldName}
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            style={{
                                                fontSize: 14,
                                                alignSelf: "left",
                                                padding: "5px 16px 5px 16px",
                                                whiteSpace: "nowrap",
                                                maxWidth: "400px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {field.ShortDescription}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            style={{
                                                fontSize: 14,
                                                alignSelf: "left",
                                                width: 175,
                                                padding: "5px 0px 5px 0px",
                                            }}
                                        >
                                            {field.LevelId == 7 && !fieldSelected(field.FieldId)
                                                ? renderAdditionalPerson(field.FieldId)
                                                : null}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                fontSize: 14,
                                                alignSelf: "left",
                                                padding: "5px 16px 5px 16px",
                                            }}
                                        >
                                            {!fieldSelected(field.FieldId) ? renderAddLink(field) : renderSelected()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            </Paper>
        </div>
    );

    return (
        <div style={{ overflow: "hidden" }}>
            <div style={{ display: "flex" }}>
                <div>
                    <TextField
                        id="custom-name"
                        label="Search Text"
                        type="text"
                        value={searchValue}
                        inputProps={{ maxLength: "150" }}
                        onChange={e => setSearchValue(e.target.value)}
                        style={{ width: "350px" }}
                        helperText="Search by Id, Name or Description"
                    />
                </div>
                <div>
                    <Button
                        id="search-fields"
                        variant="contained"
                        color="primary"
                        onClick={() => dispatch(searchEnrichmentFields(searchValue))}
                        disabled={isSearching || searchValue.length <= 2}
                        style={{ fontSize: "12px", marginLeft: "25px", marginTop: "15px" }}
                    >
                        Search
                    </Button>
                </div>
                <div>
                    <Button
                        id="clear-fields"
                        variant="contained"
                        color="secondary"
                        onClick={() => clearSearch()}
                        style={{ fontSize: "12px", marginLeft: "25px", marginTop: "15px" }}
                    >
                        Clear Results
                    </Button>
                </div>
            </div>
            <div className="m-t-md">
                {isSearching ? (
                    <div
                        style={{
                            textAlign: "center",
                            placeContent: "center",
                            marginTop: " 50px",
                            marginBottom: " 50px",
                        }}
                    >
                        <BouncingDots />
                        <h4>Fetching data...</h4>
                    </div>
                ) : (
                    <div className="m-t-md">
                        <Paper>{renderFieldTable()}</Paper>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UEFieldSearch;
